import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Row,
  Col,
  Space,
  AutoComplete,
  Typography,
  message,
} from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import {
  addPharmacyMedicineApi,
  updatePharmacyMedicineApi,
  searchPharmacyMedicinesApi,
} from "../../../services/pharmacyMedicines.services";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import HSNAndSACFormModal from "../Configurations/HSNAndSACFormModal";
import CompanyFormModal from "../Configurations/CompanyFormModal";
import CategoryFormModal from "../Configurations/CategoryFormModal";
import SaltFormModal from "../Configurations/SaltFormModal";
import { UiButton } from "../../ui";
import { debounce } from "../../../helpers/utility";
import {
  fetchMasterMedicines,
  fetchSalts,
  fetchCompanies,
  fetchCategories,
  fetchItemTypes,
  fetchHSNAndSAC,
} from "./medicineFetchers";
import styles from "../common/fieldWithAdd.module.css";
import readOnlyStyles from "../common/readOnlyField.module.css";
import {
  hsnRules,
  loosePriceRules,
  nameRules,
  positiveIntegerRules,
  rateNotAboveMrpRules,
  rateNotBelowPurchaseRules,
  RATE_FIELDS,
  moneyRules,
  MONEY_MIN,
  gstRateRules,
  revalidateGstRates,
} from "../common/validators";
import searchStyles from "../common/pharmacySearch.module.css";

const PRODUCT_TYPE_OPTIONS = [
  "Normal",
  "Service",
  "Consumable",
  "Surgical",
  "Narcotic",
  "Vaccine",
].map((v) => ({ value: v, label: v }));

const DRUG_CLASSIFICATION_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "schedule_x", label: "Schedule X" },
  { value: "habit_forming", label: "Habit Forming" },
];

const findNameClash = async (name, currentId) => {
  const text = String(name ?? "").trim();
  if (!text) return null;

  try {
    const res = await searchPharmacyMedicinesApi(
      `query=${encodeURIComponent(text)}`
    );
    const folded = text.toLowerCase();
    return (
      (res?.data || []).find(
        (item) =>
          String(item?.name ?? "").trim().toLowerCase() === folded &&
          Number(item?.record_id) !== Number(currentId)
      ) || null
    );
  } catch {
    return null;
  }
};

const SectionLabel = ({ children }) => (
  <Col span={24}>
    <Typography.Text strong style={{ display: "block", marginTop: 8, marginBottom: 4 }}>
      {children}
    </Typography.Text>
  </Col>
);


const SearchableTextField = ({
  value,
  onChange,
  onSelectOption,
  fetcher,
  placeholder,

  loadOnFocus = false,
  disabled,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const selectingRef = useRef(false);
  const requestIdRef = useRef(0);

  const handleSearch = (text) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    fetcher(text)
      .then((opts) => {
        if (requestIdRef.current !== requestId) return; 
        setOptions(
          (opts || []).map((opt) => ({
            ...opt,
            value: opt.name ?? opt.value,
            key: opt.value,
          }))
        );
      })
      .catch(() => {
        if (requestIdRef.current === requestId) setOptions([]);
      })
      .finally(() => {
        if (requestIdRef.current === requestId) setLoading(false);
      });
  };

  const optimizedSearch = useMemo(() => debounce(handleSearch, 300), [fetcher]);

  return (
    <AutoComplete
      value={value}
      disabled={disabled}
      options={options}
      placeholder={placeholder}
      onSearch={optimizedSearch}
      onFocus={() => {

        if (loadOnFocus && !value && !options.length) handleSearch("");
      }}

      notFoundContent={loading ? "Searching..." : null}
      popupMatchSelectWidth={false}
      popupClassName={searchStyles.dropdown}
      onSelect={(_, option) => {
        selectingRef.current = true;
        onSelectOption?.(option);
        onChange?.(option.name ?? option.value);
        setTimeout(() => {
          selectingRef.current = false;
        }, 0);
      }}
      onChange={(next) => {
        if (selectingRef.current) return;
        onChange?.(next);
      }}
      filterOption={false}
      allowClear
    />
  );
};

const MedicineFormModal = ({ open, setOpen, editData, refreshPage }) => {
  const [form] = Form.useForm();
  const stripConversion = Number(Form.useWatch("strip_conversion", form) || 0);
  const loosePriceTouchedRef = useRef(false);
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showAdditionalOptions, setShowAdditionalOptions] = useState(false);
  const [hsnModalOpen, setHsnModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [saltModalOpen, setSaltModalOpen] = useState(false);
  const [saltModalTarget, setSaltModalTarget] = useState(null);
  const [companyOption, setCompanyOption] = useState(null);
  const [categoryOption, setCategoryOption] = useState(null);
  /*
   * Which HSN/SAC code put the GST figures in the boxes, if one did.
   *
   * A rate that came off a code is the code's rate, not a number this form is
   * asking for, so while the field still holds that code the three boxes are
   * read-only. Typing over the code - or clearing it - means the figures no
   * longer belong to anything, and they open up again to be filled in by hand.
   */
  const [hsnRateSource, setHsnRateSource] = useState(null);
  const hsnValue = Form.useWatch("hsn_or_sac", form);
  const gstFromHsn = Boolean(hsnRateSource) && hsnValue === hsnRateSource;

  useEffect(() => {
    if (editData) {
      loosePriceTouchedRef.current = editData.loose_price != null;
      const sgst = Number(editData.sgst) || 0;
      const cgst = Number(editData.cgst) || 0;
      form.setFieldsValue({
        ...editData,
        medicine: editData.medicine,
        drug1_name: editData.drug1_name || "",
        /* IGST is the pair added up, and the box holding it is read-only - a
           medicine saved before the two were tied together would otherwise
           open on a figure that disagrees with them and cannot be reached.
           One carrying IGST and no pair is left to be written out by hand. */
        igst: sgst || cgst ? Number((sgst + cgst).toFixed(2)) : Number(editData.igst) || 0,
      });
    } else {
      form.resetFields();
      loosePriceTouchedRef.current = false;
      form.setFieldsValue({
        /* Stock that can go below zero hides a counting error rather than
           raising it, so a medicine is created without that licence. */
        allow_negative_stock: false,
        product_type: "Normal",
        is_discount_applicable: true,
        rate_override_allowed: true,
        drug_classification: "normal",
      });
    }
    setShowAdditionalOptions(false);
    setCompanyOption(null);
    setCategoryOption(null);
    /* Nothing has been picked on a form that has just opened; a saved
       medicine's rates are its own, whatever code they were first taken from. */
    setHsnRateSource(null);
  }, [editData, open]);

  const closeAndReset = () => {
    form.resetFields();
    setOpen(false);
  };

  const isInactive = Boolean(editData?.id) && editData?.is_active === false;

  const onFinish = async (values) => {
    setLoading(true);

    const nameChanged =
      String(values.name ?? "").trim().toLowerCase() !==
      String(editData?.name ?? "").trim().toLowerCase();

    if (!isInactive && (!editData?.id || nameChanged)) {
      const clash = await findNameClash(values.name, editData?.id);
      if (clash) {
        setLoading(false);
        form.setFields([
          {
            name: "name",
            errors: [
              `"${clash.name}" already exists. Names must differ by more than capitalisation.`,
            ],
          },
        ]);
        return;
      }
    }

    const payload = isInactive
      ? { is_active: values.is_active }
      : {
          ...values,
          medicine: values.medicine || undefined,
          drug1_name: values.drug1_name || null,
          /*
           * The optional price tiers go as nought rather than not at all.
           *
           * Rate B and Rate C are tiers a medicine may simply not have, which
           * is why neither is required here. Left blank they were dropped from
           * the payload altogether, and the server answered a medicine saved
           * without them with "rate_b is required" - a field the pharmacist
           * was never asked for. Nought is the answer the form already treats
           * as real for them, so that is what is sent.
           */
          rate_b: values.rate_b ?? 0,
          rate_c: values.rate_c ?? 0,
          loose_price: Number(values.strip_conversion) > 0
            ? values.loose_price ?? null
            : null,
        };

    const apiCall = editData?.id
      ? updatePharmacyMedicineApi(editData.id, payload)
      : addPharmacyMedicineApi(payload);

    apiCall
      .then((res) => {
        message.success(`Medicine ${editData?.id ? "updated" : "added"} successfully`);
        setLoading(false);
        closeAndReset();
        refreshPage(res.data);
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || "Failed to save medicine");
        setLoading(false);
      });
  };

 
  const syncLoosePrice = (changed) => {
    const rateA = Number(
      changed.rate_a !== undefined ? changed.rate_a : form.getFieldValue("rate_a")
    );
    const conversion = Number(
      changed.strip_conversion !== undefined
        ? changed.strip_conversion
        : form.getFieldValue("strip_conversion")
    );

    const hasBasis =
      Number.isFinite(rateA) && Number.isFinite(conversion) && conversion > 0;

    if (hasBasis && !loosePriceTouchedRef.current) {
      form.setFieldValue("loose_price", Number((rateA / conversion).toFixed(2)));
    }


    form.validateFields(["loose_price"]).catch(() => {});
  };

 
  const applyHsnRates = (option) => {
    const code = option?.raw;
    if (!code) return;

    const num = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const round2 = (value) => Number(value.toFixed(2));

    /*
     * One rate, written out the way the module writes every rate: an equal
     * pair and the IGST they add up to.
     *
     * The code carries whichever spelling it was created with - the pair
     * where it has one, IGST where it does not - and the total is the same
     * figure either way. It is then halved rather than copied across, so a
     * code made before the pair had to be a pair cannot put 3 and 4 into two
     * boxes that are about to be locked against correction. The tax charged
     * is unchanged; only the half it is written in each box is.
     */
    const cgst = num(code.cgst);
    const sgst = num(code.sgst);
    const total = cgst || sgst ? cgst + sgst : num(code.igst);
    const half = round2(total / 2);

    form.setFieldsValue({
      cgst: half,
      sgst: half,
      igst: round2(half * 2),
    });
    setHsnRateSource(code.tax_code ?? null);
    /* A code carrying a lopsided pair is a code with a typo in it, and saying
       so when it is picked beats saying so when the medicine is saved. */
    revalidateGstRates(form);
  };

  const syncIgst = (changed) => {
    const sgst = Number(
      changed.sgst !== undefined ? changed.sgst : form.getFieldValue("sgst")
    );
    const cgst = Number(
      changed.cgst !== undefined ? changed.cgst : form.getFieldValue("cgst")
    );
    const total = (Number.isFinite(sgst) ? sgst : 0) + (Number.isFinite(cgst) ? cgst : 0);
    form.setFieldValue("igst", Number(total.toFixed(2)));
    /* The pair is judged against itself, so the box that was not touched has
       to be asked again - otherwise "CGST and SGST must be equal" stays up on
       it after the two have been made equal. */
    revalidateGstRates(form);
  };

  const handleMedicineSelect = (option) => {
    form.setFieldsValue({
      medicine: option.key ?? option.value,
      name: option.name ?? option.value,
      drug1_name: option.drug1_name || "",
    });
  };

  return (
    <Drawer
      title={editData?.id ? "Edit Medicine" : "Add Medicine"}
      open={open}
      onClose={closeAndReset}
      width={720}
      destroyOnClose
      footer={
        <Space style={{ display: "flex", justifyContent: "flex-end" }}>
          <UiButton variant="secondary" onClick={closeAndReset}>
            Cancel
          </UiButton>
          <UiButton loading={loading} onClick={() => formRef.current?.submit()}>
            Save
          </UiButton>
        </Space>
      }
    >
      {isInactive && (
        <Alert
          type="info"
          showIcon
          message="This medicine is inactive"
          description="Details are read-only. Switch it to Active and save to edit them."
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        layout="vertical"
        form={form}
        ref={formRef}
        onFinish={onFinish}
        disabled={isInactive}
      >
        <Form.Item name="medicine" hidden>
          <Input />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Name"
              name="name"
              rules={isInactive ? [] : nameRules("Medicine")}
            >
              <SearchableTextField
                fetcher={fetchMasterMedicines}
                placeholder="Search or type medicine name"
                onSelectOption={handleMedicineSelect}
                onChange={(value) => {
                  form.setFieldsValue({ name: value, medicine: undefined });
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Drug Name" name="drug1_name">
              <div className={styles.fieldWithAdd}>
                <SearchableTextField
                  fetcher={fetchSalts}
                  placeholder="Search or type salt"
                  value={Form.useWatch("drug1_name", form)}
                  onChange={(value) => form.setFieldValue("drug1_name", value)}
                />
                <UiButton
                  onClick={() => {
                    setSaltModalTarget("drug1_name");
                    setSaltModalOpen(true);
                  }}
                >
                  +
                </UiButton>
              </div>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Rack Number" name="rack_no">
              <Input placeholder="Rack Number" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Packing" name="packing">
              <Input placeholder="e.g. 1x10" maxLength={50} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Unit" name="unit">
              <Input placeholder="e.g. ml, strip, tablet" maxLength={50} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Fast Search Keyword" name="fast_search_keyword">
              <Input placeholder="Fast Search Keyword" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Item Type" name="item_type">
              <AsyncSearchSelect
                fetcher={fetchItemTypes}
                loadOnOpen
                placeholder="Search item type"
                initialOption={
                  editData?.item_type
                    ? { value: editData.item_type, label: editData.item_type_name }
                    : undefined
                }
                onChange={(value) => form.setFieldValue("item_type", value)}
                value={Form.useWatch("item_type", form)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Product Type" name="product_type">
              <Select options={PRODUCT_TYPE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="HSN/SAC" name="hsn_or_sac" rules={hsnRules()}>
              <div className={styles.fieldWithAdd}>
                <SearchableTextField
                  fetcher={fetchHSNAndSAC}
                  loadOnFocus
                  placeholder="Search or type HSN/SAC"
                  value={hsnValue}
                  onChange={(value) => form.setFieldValue("hsn_or_sac", value)}
                  onSelectOption={applyHsnRates}
                />
                <UiButton onClick={() => setHsnModalOpen(true)}>+</UiButton>
              </div>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Company" name="company">
              <div className={styles.fieldWithAdd}>
                <AsyncSearchSelect
                  fetcher={fetchCompanies}
                  loadOnOpen
                  placeholder="Search company"
                  initialOption={
                    companyOption ||
                    (editData?.company
                      ? { value: editData.company, label: editData.company_name }
                      : undefined)
                  }
                  onChange={(value) => form.setFieldValue("company", value)}
                  value={Form.useWatch("company", form)}
                />
                <UiButton onClick={() => setCompanyModalOpen(true)}>+</UiButton>
              </div>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Category" name="category">
              <div className={styles.fieldWithAdd}>
                <AsyncSearchSelect
                  fetcher={fetchCategories}
                  loadOnOpen
                  placeholder="Search category"
                  initialOption={
                    categoryOption ||
                    (editData?.category
                      ? { value: editData.category, label: editData.category_name }
                      : undefined)
                  }
                  onChange={(value) => form.setFieldValue("category", value)}
                  value={Form.useWatch("category", form)}
                />
                <UiButton onClick={() => setCategoryModalOpen(true)}>+</UiButton>
              </div>
            </Form.Item>
          </Col>

          {/*
            * A sale is either inside the state or across it, never both, so
            * the two switches answer one question between them - turning
            * either on turns the other off rather than leaving a medicine
            * claiming to be taxed twice.
            */}
          <Col span={6}>
            <Form.Item
              label="Intra-state GST Applicable"
              name="is_local_gst_applicable"
              valuePropName="checked"
            >
              <Switch
                onChange={(on) => {
                  if (on) form.setFieldValue("is_central_gst_applicable", false);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Central GST Applicable"
              name="is_central_gst_applicable"
              valuePropName="checked"
            >
              <Switch
                onChange={(on) => {
                  if (on) form.setFieldValue("is_local_gst_applicable", false);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Allow Negative Stock" name="allow_negative_stock" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          {editData?.id && (
            <Col span={6}>
              <Form.Item label="Active" name="is_active" valuePropName="checked">
                <Switch disabled={false} />
              </Form.Item>
            </Col>
          )}

          <Col span={6}>
            <Form.Item label="SGST %" name="sgst" rules={gstRateRules(form, "sgst")}>
              {/*
                * Read-only with the steppers taken away, not merely read-only:
                * antd leaves the up and down arrows live on a read-only
                * InputNumber, so a box that refuses to be typed in could still
                * be clicked up and down past the rate its code carries.
                */}
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                readOnly={gstFromHsn}
                controls={!gstFromHsn}
                className={gstFromHsn ? readOnlyStyles.readOnly : undefined}
                onChange={(value) => syncIgst({ sgst: value })}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="CGST %" name="cgst" rules={gstRateRules(form, "cgst")}>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                readOnly={gstFromHsn}
                controls={!gstFromHsn}
                className={gstFromHsn ? readOnlyStyles.readOnly : undefined}
                onChange={(value) => syncIgst({ cgst: value })}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="IGST %" name="igst" rules={gstRateRules(form, "igst")}>
              {/* Always the two above added together, whatever put them there. */}
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                readOnly
                controls={false}
                className={readOnlyStyles.readOnly}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Cess %" name="cess">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item label="MRP" name="mrp" rules={moneyRules("MRP")}>
              <InputNumber style={{ width: "100%" }} min={MONEY_MIN}
                onChange={() =>
                  form.validateFields(RATE_FIELDS).catch(() => {})
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Purchase Rate"
              name="purchase_rate"
              rules={[
                ...moneyRules("Purchase rate"),
                ...rateNotAboveMrpRules(form, "Purchase rate"),
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={MONEY_MIN}
                onChange={() => form.validateFields(RATE_FIELDS).catch(() => {})}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Rate A"
              name="rate_a"
              rules={[
                ...moneyRules("Rate A", { required: !isInactive }),
                ...rateNotAboveMrpRules(form, "Rate A"),
                ...rateNotBelowPurchaseRules(form, "Rate A"),
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={MONEY_MIN}
                onChange={(value) => syncLoosePrice({ rate_a: value })}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            {/* Rate B and Rate C are optional price tiers that a medicine
                may simply not have, so nought is a real answer for them -
                they are held at or above zero rather than above it. */}
            <Form.Item label="Rate B" name="rate_b"
              rules={[
                ...rateNotAboveMrpRules(form, "Rate B"),
                ...rateNotBelowPurchaseRules(form, "Rate B"),
              ]}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Rate C" name="rate_c"
              rules={[
                ...rateNotAboveMrpRules(form, "Rate C"),
                ...rateNotBelowPurchaseRules(form, "Rate C"),
              ]}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Strip Conversion"
              name="strip_conversion"
              rules={positiveIntegerRules("Strip conversion")}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={1}
                placeholder="Tablets per strip"
                onChange={(value) => syncLoosePrice({ strip_conversion: value })}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
  
            <Form.Item
              label="Loose Price"
              name="loose_price"
              rules={[...moneyRules("Loose price"), ...loosePriceRules(form)]}
              tooltip="Price of a single tablet sold out of a strip. Filled in from Rate A divided by the strip conversion, and can be overridden."
            >
              <InputNumber
                style={{ width: "100%" }}
                min={MONEY_MIN}
                precision={2}
                disabled={isInactive || stripConversion <= 0}
                placeholder={
                  stripConversion > 0 ? undefined : "Needs a strip conversion"
                }
                onChange={() => {
                  loosePriceTouchedRef.current = true;
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Stockout Limit" name="stockout_limit">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
        </Row>

        <UiButton
          variant="text"
          onClick={() => setShowAdditionalOptions((prev) => !prev)}
          style={{ paddingInline: 0, marginBottom: showAdditionalOptions ? 8 : 0 }}
        >
          {showAdditionalOptions ? <UpOutlined /> : <DownOutlined />} Additional Options
        </UiButton>

        {showAdditionalOptions && (
          <Row gutter={16}>
            <SectionLabel>Inventory & Reorder</SectionLabel>
            <Col span={6}>
              <Form.Item label="Minimum Quantity" name="minimum_qty">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Maximum Quantity" name="maximum_qty">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Reorder Quantity" name="reorder_qty">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Days" name="reorder_days">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>

            <SectionLabel>Discounts</SectionLabel>
            <Col span={6}>
              <Form.Item label="Volume Discount" name="volume_discount">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="On Quantity." name="volume_discount_on_qty">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Item Discount" name="item_discount">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Maximum Discount %" name="maximum_discount_percent">
                <InputNumber style={{ width: "100%" }} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Minimum Margin %" name="minimum_margin_percent">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Special Discount" name="special_discount">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Purchase Discount" name="purchase_discount">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Discount Less" name="discount_less">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="On Quantity" name="discount_on_quantity">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Discount Applicable" name="is_discount_applicable" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>

            <SectionLabel>Regulatory</SectionLabel>
            <Col span={6}>
              <Form.Item label="Narcotic" name="is_narcotic" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Schedule H" name="is_schedule_h" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Schedule H1" name="is_schedule_h1" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Classification" name="drug_classification">
                <Select options={DRUG_CLASSIFICATION_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="F6/Rate Override" name="rate_override_allowed" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>

      {hsnModalOpen && (
        <HSNAndSACFormModal
          open={hsnModalOpen}
          setOpen={setHsnModalOpen}
          onCreated={(created) => {
            form.setFieldValue("hsn_or_sac", created.tax_code);
            /* A code made from here is chosen by the act of making it, so it
               fills the tax fields exactly as picking an existing one does. */
            applyHsnRates({ raw: created });
          }}
        />
      )}

      {companyModalOpen && (
        <CompanyFormModal
          open={companyModalOpen}
          setOpen={setCompanyModalOpen}
          onCreated={(created) => {
            setCompanyOption({ value: created.id, label: created.name });
            form.setFieldValue("company", created.id);
          }}
        />
      )}

      {categoryModalOpen && (
        <CategoryFormModal
          open={categoryModalOpen}
          setOpen={setCategoryModalOpen}
          onCreated={(created) => {
            setCategoryOption({ value: created.id, label: created.name });
            form.setFieldValue("category", created.id);
          }}
        />
      )}

      {saltModalOpen && (
        <SaltFormModal
          open={saltModalOpen}
          setOpen={setSaltModalOpen}
          onCreated={(created) => {
            if (saltModalTarget) form.setFieldValue(saltModalTarget, created.name);
          }}
        />
      )}
    </Drawer>
  );
};

export default MedicineFormModal;
